import request from '@Utils/request';
import {
  GroupNode,
  PocNodeType,
  PocTargetSearchType,
  UserNode,
} from '@Dashboard/ProvisionModal/PocTransFer/type';
import { IdSyncMode, TargetType } from '@Types/provision';
import {
  getLocalStorageItemWithExpiry,
  setLocalStorageItemWithExpiry,
} from '../../utils/common';
import { AxiosPromise } from 'axios';

interface TargetUser {
  title: string;
  key: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  target: object;
}

interface TargetGroup {
  title: string;
  key: string;
  siteId?: string | null; // for sharepoint
  teamId?: string | null; // for teams
}

interface SearchResult {
  targets: TargetUser[];
  targetGroups: TargetGroup[];
  [k: string]: any;
}

async function _PocSearchUser(
  keyword: string,
  orgId: string
): Promise<Pick<UserNode, 'key' | 'title' | 'type' | 'target'>[]> {
  const res: SearchResult = (
    await request({
      url: 'users/services/exchange/targets/poc/search',
      method: 'POST',
      data: {
        searchType: PocTargetSearchType.TargetUser,
        searchString: keyword,
      },
      headers: { 'x-org-id': orgId },
    })
  ).data;
  return res.targets.map((target) => {
    return {
      key: target.key,
      title: target.title,
      type: PocNodeType.User,
      target: target.target,
    };
  });
}

async function _PocSearchGroup(
  keyword: string,
  orgId: string
): Promise<Pick<GroupNode, 'key' | 'title' | 'type'>[]> {
  const res: SearchResult = (
    await request({
      url: 'users/services/exchange/targets/poc/search',
      method: 'POST',
      data: {
        searchType: PocTargetSearchType.TargetGroup,
        searchString: keyword,
      },
      headers: { 'x-org-id': orgId },
    })
  ).data;
  return res.targetGroups.map((target) => {
    return {
      key: target.key,
      title: target.title,
      type: PocNodeType.Group,
    };
  });
}

function apiSearchUserOrGroup(
  k: string,
  mode: PocTargetSearchType.TargetUser,
  orgId: string
): ReturnType<typeof _PocSearchUser>;

function apiSearchUserOrGroup(
  k: string,
  mode: PocTargetSearchType.TargetGroup,
  orgId: string
): ReturnType<typeof _PocSearchGroup>;

function apiSearchUserOrGroup(
  keyword: string,
  mode: PocTargetSearchType,
  orgId: string
) {
  if (mode === PocTargetSearchType.TargetUser) {
    return _PocSearchUser(keyword, orgId);
  } else {
    return _PocSearchGroup(keyword, orgId);
  }
}

async function apiGetUsersByGroupKey(
  groupId: string,
  orgId: string
): Promise<Pick<UserNode, 'key' | 'title' | 'type' | 'target'>[]> {
  const res: SearchResult = (
    await request({
      url: `users/services/exchange/targets/poc/groups/${groupId}`,
      method: 'GET',
      headers: { 'x-org-id': orgId },
    })
  ).data;
  return res.targets.map((target) => {
    return {
      key: target.key,
      title: target.title,
      type: PocNodeType.User,
      target: target.target,
    };
  });
}

function apiTriggerPocIdSync(userTargets: any[], orgId: string) {
  return request({
    url: 'services/exchange/targets/sync',
    method: 'post',
    data: {
      syncMode: IdSyncMode.Poc,
      targetType: TargetType.User,
      users: userTargets,
    },
    headers: { 'x-org-id': orgId },
  });
}

const POC_CHECK_PREFIX = 'isPoc_';
async function apiCheckIsPoc(orgId: string): Promise<boolean> {
  const cacheKey = POC_CHECK_PREFIX + orgId;
  let isPoc: boolean | null = getLocalStorageItemWithExpiry(cacheKey);
  if (typeof isPoc === 'boolean') {
    return isPoc;
  }

  isPoc = (
    await request({
      url: 'apps/exchange/provision/checkPoc',
      method: 'post',
    })
  ).data as boolean;
  setLocalStorageItemWithExpiry(cacheKey, isPoc, 1000 * 60 * 3);

  return isPoc;
}

function pocUpgrade(orgId: string) {
  localStorage.removeItem(POC_CHECK_PREFIX + orgId);
  return request({
    method: 'POST',
    url: 'apps/exchange/provision/poc/upgrade',
    headers: { 'x-org-id': orgId },
  });
}

function getAllOrgPocStatus(): AxiosPromise<{
  [orgId: string]:
    | {
        exchange?: boolean;
      }
    | undefined;
}> {
  return request({
    method: 'GET',
    url: 'apps/provisioned/poc',
    headers: { 'x-org-id': 'all' },
  });
}

function getPocUsers(orgId: string): AxiosPromise<{
  isPocProvisioned: boolean;
  limit: number;
  users: { id: string; displayName: string; mail: string }[];
}> {
  return request({
    method: 'GET',
    url: 'users/services/exchange/targets/poc/users/list',
    headers: { 'x-org-id': orgId },
  });
}

function extendPocUsers(orgId: string, users: any[]) {
  return request({
    method: 'POST',
    url: 'users/services/exchange/targets/poc/users/add',
    headers: { 'x-org-id': orgId },
    data: {
      users,
    },
  });
}

function deletePocUsers(
  orgId: string,
  users: {
    id: string;
    displayName: string;
    mail: string;
  }[]
) {
  return request({
    method: 'POST',
    url: 'users/services/exchange/targets/poc/users/delete',
    headers: { 'x-org-id': orgId },
    data: {
      users,
    },
  });
}

function importUsers(orgId: string, upns: string[]) {
  return request({
    method: 'POST',
    url: 'users/services/exchange/targets/poc/users/import',
    headers: { 'x-org-id': orgId },
    data: {
      upns,
    },
  });
}

export default {
  apiTriggerPocIdSync,
  apiGetUsersByGroupKey,
  apiSearchUserOrGroup,
  apiCheckIsPoc,
  pocUpgrade,
  getAllOrgPocStatus,
  getPocUsers,
  extendPocUsers,
  deletePocUsers,
  importUsers,
};
